import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  FaShip,
  FaGlobe,
  FaClipboardList,
  FaBell,
  FaSearch,
  FaFilter,
  FaEye,
  FaCheckCircle,
  FaFileInvoice,
  FaChartLine,
  FaDollarSign,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";

const UserDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userName] = useState("John Doe");

  // Sample shipment data
  const [shipments] = useState([
    {
      id: "TRK-12345",
      origin: "Los Angeles, CA",
      destination: "Tokyo, Japan",
      status: "In Transit",
      date: "2024-01-15",
      type: "Ocean Freight",
      estimatedDelivery: "2024-01-25",
    },
    {
      id: "TRK-12346",
      origin: "New York, NY",
      destination: "London, UK",
      status: "Delivered",
      date: "2024-01-10",
      type: "Air Freight",
      estimatedDelivery: "2024-01-18",
    },
    {
      id: "TRK-12347",
      origin: "Shanghai, China",
      destination: "Los Angeles, CA",
      status: "Customs Clearance",
      date: "2024-01-12",
      type: "Ocean Freight",
      estimatedDelivery: "2024-01-28",
    },
    {
      id: "TRK-12348",
      origin: "Hamburg, Germany",
      destination: "Chicago, IL",
      status: "In Transit",
      date: "2024-01-14",
      type: "Intermodal",
      estimatedDelivery: "2024-01-22",
    },
    {
      id: "TRK-12349",
      origin: "Singapore",
      destination: "Sydney, Australia",
      status: "Processing",
      date: "2024-01-16",
      type: "Air Freight",
      estimatedDelivery: "2024-01-20",
    },
  ]);

  // Statistics data
  const stats = [
    {
      title: "Active Shipments",
      value: "8",
      icon: <FaShip />,
      color: "#D4AF37",
      change: "+2",
    },
    {
      title: "Delivered This Month",
      value: "23",
      icon: <FaCheckCircle />,
      color: "#4CAF50",
      change: "+5",
    },
    {
      title: "Total Shipments",
      value: "156",
      icon: <FaClipboardList />,
      color: "#0B1E33",
      change: "+12",
    },
    {
      title: "Countries Served",
      value: "25",
      icon: <FaGlobe />,
      color: "#D4AF37",
      change: "0",
    },
  ];

  // Quote requests
  const quoteRequests = [
    {
      id: "QT-001",
      type: "Ocean Freight",
      from: "New York",
      to: "London",
      status: "Pending",
      date: "2024-01-16",
    },
    {
      id: "QT-002",
      type: "Air Freight",
      from: "Los Angeles",
      to: "Tokyo",
      status: "Quoted",
      date: "2024-01-15",
    },
    {
      id: "QT-003",
      type: "Warehousing",
      from: "Chicago",
      to: "Toronto",
      status: "Pending",
      date: "2024-01-14",
    },
  ];

  // Recent activities
  const activities = [
    {
      id: 1,
      action: "Shipment TRK-12346 delivered",
      time: "2 hours ago",
      type: "success",
    },
    {
      id: 2,
      action: "Quote QT-002 approved",
      time: "5 hours ago",
      type: "info",
    },
    {
      id: 3,
      action: "Payment received for shipment TRK-12344",
      time: "1 day ago",
      type: "success",
    },
    {
      id: 4,
      action: "Shipment TRK-12345 cleared customs",
      time: "2 days ago",
      type: "warning",
    },
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered":
        return "#4CAF50";
      case "In Transit":
        return "#D4AF37";
      case "Customs Clearance":
        return "#FF9800";
      case "Processing":
        return "#2196F3";
      default:
        return "#9E9E9E";
    }
  };

  const getQuoteStatusColor = (status) => {
    switch (status) {
      case "Quoted":
        return "#4CAF50";
      case "Pending":
        return "#FF9800";
      default:
        return "#9E9E9E";
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <div className="spinner"></div>
        <p>Loading your dashboard...</p>
      </LoadingContainer>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard | Trans World Shipping</title>
        <meta
          name="description"
          content="Manage your shipments, track deliveries, and access your shipping dashboard."
        />
      </Helmet>

      <TheDashboard>
        <div className="dashboard-container">
          {/* Welcome Section */}
          <div className="welcome-section">
            <div className="welcome-text">
              <h1>Welcome back, {userName}!</h1>
              <p>Here's what's happening with your shipments today.</p>
            </div>
            <div className="quick-actions">
              <button
                className="action-btn primary"
                onClick={() => navigate("/rate-request")}
              >
                Request a Quote
              </button>
              <button
                className="action-btn secondary"
                onClick={() => navigate("/track")}
              >
                Track Shipment
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div className="stat-card" key={index}>
                <div
                  className="stat-icon"
                  style={{
                    backgroundColor: `${stat.color}15`,
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </div>
                <div className="stat-info">
                  <h3>{stat.value}</h3>
                  <p>{stat.title}</p>
                  <span className="stat-change positive">{stat.change}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Main Content Grid */}
          <div className="main-grid">
            {/* Recent Shipments */}
            <div className="recent-shipments">
              <div className="section-header">
                <h2>Recent Shipments</h2>
                <div className="header-actions">
                  <button className="icon-btn">
                    <FaSearch />
                  </button>
                  <button className="icon-btn">
                    <FaFilter />
                  </button>
                  <Link to="/track" className="view-all">
                    View All
                  </Link>
                </div>
              </div>

              <div className="shipments-table">
                <table>
                  <thead>
                    <tr>
                      <th>Tracking ID</th>
                      <th>Origin → Destination</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Est. Delivery</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.map((shipment) => (
                      <tr key={shipment.id}>
                        <td className="tracking-id">{shipment.id}</td>
                        <td>
                          <div className="route">
                            <span>{shipment.origin.split(",")[0]}</span>
                            <span className="arrow">→</span>
                            <span>{shipment.destination.split(",")[0]}</span>
                          </div>
                        </td>
                        <td>{shipment.type}</td>
                        <td>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: getStatusColor(shipment.status),
                              color: "white",
                            }}
                          >
                            {shipment.status}
                          </span>
                        </td>
                        <td>{shipment.estimatedDelivery}</td>
                        <td>
                          <button
                            className="view-btn"
                            onClick={() => navigate(`/track/${shipment.id}`)}
                          >
                            <FaEye /> Track
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="sidebar">
              {/* Quote Requests */}
              <div className="quote-requests">
                <div className="section-header">
                  <h2>Quote Requests</h2>
                  <Link to="/rate-request" className="view-all">
                    New Quote
                  </Link>
                </div>
                {quoteRequests.map((quote) => (
                  <div className="quote-item" key={quote.id}>
                    <div className="quote-info">
                      <span className="quote-id">{quote.id}</span>
                      <span className="quote-route">
                        {quote.from} → {quote.to}
                      </span>
                      <span className="quote-type">{quote.type}</span>
                    </div>
                    <div className="quote-status">
                      <span
                        className="status-badge small"
                        style={{
                          backgroundColor: getQuoteStatusColor(quote.status),
                        }}
                      >
                        {quote.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recent Activity */}
              <div className="recent-activity">
                <div className="section-header">
                  <h2>Recent Activity</h2>
                  <FaBell className="bell-icon" />
                </div>
                <div className="activity-list">
                  {activities.map((activity) => (
                    <div className="activity-item" key={activity.id}>
                      <div className={`activity-dot ${activity.type}`}></div>
                      <div className="activity-content">
                        <p>{activity.action}</p>
                        <span className="activity-time">{activity.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="quick-stats">
                <div className="stat-item">
                  <FaFileInvoice className="stat-icon-small" />
                  <div className="stat-details">
                    <span className="stat-label">Pending Invoices</span>
                    <span className="stat-value">3</span>
                  </div>
                </div>
                <div className="stat-item">
                  <FaDollarSign className="stat-icon-small" />
                  <div className="stat-details">
                    <span className="stat-label">Total Spent (MTD)</span>
                    <span className="stat-value">$12,450</span>
                  </div>
                </div>
                <div className="stat-item">
                  <FaChartLine className="stat-icon-small" />
                  <div className="stat-details">
                    <span className="stat-label">Savings This Year</span>
                    <span className="stat-value">$8,230</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="quick-links">
            <h3>Quick Links</h3>
            <div className="links-grid">
              <Link to="/rate-request" className="quick-link">
                Get a Rate Quote
              </Link>
              <Link to="/equipment" className="quick-link">
                View Equipment
              </Link>
              <Link to="/contact" className="quick-link">
                Contact Support
              </Link>
              <Link to="/profile" className="quick-link">
                My Profile
              </Link>
            </div>
          </div>
        </div>
      </TheDashboard>
    </>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;

  .spinner {
    width: 50px;
    height: 50px;
    border: 3px solid #e2e8f0;
    border-top-color: #d4af37;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  p {
    margin-top: 1rem;
    color: #718096;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TheDashboard = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f7fa;
  padding: 2rem;

  .dashboard-container {
    max-width: 1400px;
    margin: 0 auto;
  }

  /* Welcome Section */
  .welcome-section {
    background: linear-gradient(135deg, #0b1e33 0%, #0a1a2c 100%);
    border-radius: 20px;
    padding: 2rem;
    margin-bottom: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;

    .welcome-text {
      h1 {
        font-size: 1.8rem;
        color: white;
        margin-bottom: 0.5rem;
        font-weight: 700;
      }

      p {
        color: rgba(255, 255, 255, 0.8);
        font-size: 0.95rem;
      }
    }

    .quick-actions {
      display: flex;
      gap: 1rem;

      .action-btn {
        padding: 0.75rem 1.5rem;
        border-radius: 10px;
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: inherit;

        &.primary {
          background: #d4af37;
          color: #0b1e33;
          border: none;

          &:hover {
            background: #c4a12e;
            transform: translateY(-2px);
          }
        }

        &.secondary {
          background: transparent;
          color: white;
          border: 1px solid #d4af37;

          &:hover {
            background: rgba(212, 175, 55, 0.1);
            transform: translateY(-2px);
          }
        }
      }
    }
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1.5rem;
    margin-bottom: 2rem;

    .stat-card {
      background: white;
      border-radius: 16px;
      padding: 1.25rem;
      display: flex;
      align-items: center;
      gap: 1rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
      }

      .stat-info {
        flex: 1;

        h3 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #0b1e33;
          margin-bottom: 0.25rem;
        }

        p {
          font-size: 0.85rem;
          color: #718096;
          margin-bottom: 0.25rem;
        }

        .stat-change {
          font-size: 0.75rem;
          font-weight: 600;

          &.positive {
            color: #4caf50;
          }
        }
      }
    }
  }

  /* Main Grid */
  .main-grid {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 2rem;
    margin-bottom: 2rem;
  }

  /* Recent Shipments */
  .recent-shipments {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;

      h2 {
        font-size: 1.3rem;
        color: #0b1e33;
        font-weight: 700;
      }

      .header-actions {
        display: flex;
        gap: 0.5rem;
        align-items: center;

        .icon-btn {
          background: #f8f9fa;
          border: none;
          padding: 0.5rem;
          border-radius: 8px;
          cursor: pointer;
          color: #718096;
          transition: all 0.3s ease;

          &:hover {
            background: #d4af37;
            color: white;
          }
        }

        .view-all {
          color: #d4af37;
          text-decoration: none;
          font-size: 0.85rem;
          font-weight: 500;

          &:hover {
            text-decoration: underline;
          }
        }
      }
    }

    .shipments-table {
      overflow-x: auto;

      table {
        width: 100%;
        border-collapse: collapse;

        th {
          text-align: left;
          padding: 0.75rem;
          background: #f8f9fa;
          color: #4a5568;
          font-size: 0.85rem;
          font-weight: 600;
        }

        td {
          padding: 0.75rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.85rem;
          color: #4a5568;
        }

        .tracking-id {
          font-weight: 600;
          color: #0b1e33;
        }

        .route {
          display: flex;
          align-items: center;
          gap: 0.5rem;

          .arrow {
            color: #d4af37;
          }
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-block;
        }

        .view-btn {
          background: none;
          border: none;
          color: #d4af37;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.8rem;

          &:hover {
            color: #c4a12e;
          }
        }
      }
    }
  }

  /* Sidebar */
  .sidebar {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  /* Quote Requests */
  .quote-requests {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      h2 {
        font-size: 1.1rem;
        color: #0b1e33;
        font-weight: 700;
      }

      .view-all {
        color: #d4af37;
        text-decoration: none;
        font-size: 0.8rem;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .quote-item {
      padding: 0.75rem 0;
      border-bottom: 1px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: center;

      &:last-child {
        border-bottom: none;
      }

      .quote-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .quote-id {
          font-weight: 600;
          color: #0b1e33;
          font-size: 0.85rem;
        }

        .quote-route {
          font-size: 0.75rem;
          color: #718096;
        }

        .quote-type {
          font-size: 0.7rem;
          color: #d4af37;
        }
      }

      .status-badge.small {
        padding: 0.2rem 0.6rem;
        font-size: 0.7rem;
      }
    }
  }

  /* Recent Activity */
  .recent-activity {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      h2 {
        font-size: 1.1rem;
        color: #0b1e33;
        font-weight: 700;
      }

      .bell-icon {
        color: #d4af37;
      }
    }

    .activity-list {
      .activity-item {
        display: flex;
        gap: 0.75rem;
        padding: 0.75rem 0;
        border-bottom: 1px solid #e2e8f0;

        &:last-child {
          border-bottom: none;
        }

        .activity-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          margin-top: 0.25rem;

          &.success {
            background: #4caf50;
          }

          &.info {
            background: #2196f3;
          }

          &.warning {
            background: #ff9800;
          }
        }

        .activity-content {
          flex: 1;

          p {
            font-size: 0.85rem;
            color: #4a5568;
            margin-bottom: 0.25rem;
          }

          .activity-time {
            font-size: 0.7rem;
            color: #a0aec0;
          }
        }
      }
    }
  }

  /* Quick Stats */
  .quick-stats {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .stat-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 0.75rem 0;

      &:not(:last-child) {
        border-bottom: 1px solid #e2e8f0;
      }

      .stat-icon-small {
        font-size: 1.25rem;
        color: #d4af37;
      }

      .stat-details {
        flex: 1;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .stat-label {
          font-size: 0.85rem;
          color: #718096;
        }

        .stat-value {
          font-size: 1rem;
          font-weight: 700;
          color: #0b1e33;
        }
      }
    }
  }

  /* Quick Links */
  .quick-links {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    h3 {
      font-size: 1.1rem;
      color: #0b1e33;
      margin-bottom: 1rem;
      font-weight: 700;
    }

    .links-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;

      .quick-link {
        padding: 0.6rem;
        text-align: center;
        background: #f8f9fa;
        color: #0b1e33;
        text-decoration: none;
        border-radius: 8px;
        font-size: 0.85rem;
        transition: all 0.3s ease;

        &:hover {
          background: #d4af37;
          color: white;
          transform: translateY(-2px);
        }
      }
    }
  }

  /* Responsive */
  @media screen and (max-width: 1200px) {
    padding: 1.5rem;

    .stats-grid {
      gap: 1rem;
    }
  }

  @media screen and (max-width: 1024px) {
    .main-grid {
      grid-template-columns: 1fr;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .quick-links .links-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media screen and (max-width: 768px) {
    padding: 1rem;

    .welcome-section {
      flex-direction: column;
      text-align: center;

      .quick-actions {
        width: 100%;
        flex-direction: column;

        .action-btn {
          width: 100%;
        }
      }
    }

    .stats-grid {
      grid-template-columns: 1fr;
    }

    .quick-links .links-grid {
      grid-template-columns: 1fr;
    }

    .shipments-table {
      font-size: 0.75rem;

      table th,
      table td {
        padding: 0.5rem;
      }
    }
  }
`;

export default UserDashboard;
